import * as React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerTrigger,
} from './ui/drawer.tsx'
import { HamburgerButton } from './ui/hamburger-button.tsx'
import { Icon } from './ui/icon.tsx'

// SSR renders the desktop variant; the client corrects after hydration.
const mobileQuery = '(max-width: 767px)'
function subscribeToMobile(callback: () => void) {
	const mql = window.matchMedia(mobileQuery)
	mql.addEventListener('change', callback)
	return () => mql.removeEventListener('change', callback)
}
function useIsMobile() {
	return React.useSyncExternalStore(
		subscribeToMobile,
		() => window.matchMedia(mobileQuery).matches,
		() => false,
	)
}

const emptySubscribe = () => () => {}
/** true after hydration — portals can only render on the client */
function useIsClient() {
	return React.useSyncExternalStore(
		emptySubscribe,
		() => true,
		() => false,
	)
}

/**
 * Site navigation behind a hamburger icon. On desktop the trigger sits in the
 * header and opens a left-side drawer; on mobile the trigger is a fixed,
 * thumb-friendly button at the bottom right and the drawer opens as a bottom
 * sheet. Uses the same Vaul based Drawer component we already ship.
 *
 * Usage:
 *   <MobileNav links={[{to:'/', label:'Home'}]} />
 */
export function MobileNav({
	links,
	user,
	isAdmin,
}: {
	links: Array<{ to: string; label: React.ReactNode }>
	user?: { id: string; name: string | null; username: string; image?: { objectKey: string } | null } | null
	isAdmin?: boolean
}) {
	const [open, setOpen] = React.useState(false)
	const { t } = useTranslation()
	const isMobile = useIsMobile()
	const isClient = useIsClient()

	return (
		<Drawer direction={isMobile ? 'bottom' : 'left'} open={open} onOpenChange={setOpen}>
			{/* Header trigger (desktop only — mobile gets the fixed bottom
			    button below) */}
			<DrawerTrigger asChild>
				<HamburgerButton open={open} className="max-md:hidden" />
			</DrawerTrigger>

			{/* Mobile trigger: fixed to the bottom right, within thumb reach,
			    inside the museum-mat frame and above the iOS home bar. Portaled
			    to <body> because the header's backdrop-filter would otherwise
			    become the containing block for position:fixed. */}
			{isClient &&
				createPortal(
					<HamburgerButton
						open={open}
						onClick={() => setOpen(true)}
						className="fixed right-[calc(var(--frame-w)+10px)] bottom-[calc(var(--frame-w)+10px+env(safe-area-inset-bottom))] z-50 size-14 rounded-full shadow-lg md:hidden"
						style={{
							background: 'var(--paper)',
							border: '1px solid var(--line)',
							color: 'var(--ink)',
						}}
					/>,
					document.body,
				)}

			{/* Drawer panel */}
			<DrawerContent
				className={
					isMobile
						? 'max-h-[85vh] pb-[env(safe-area-inset-bottom)]'
						: 'w-[min(440px,calc(100vw-2*var(--frame-w)))] pt-0 sm:max-w-[440px]'
				}
				style={{ background: 'var(--paper)' }}
			>
				<nav className="flex h-full flex-col overflow-y-auto px-10 py-8 max-md:px-6 max-md:py-5">
					{/* Header */}
					<div className="mb-16 flex items-center justify-between max-md:mb-6">
						<span className="eyebrow">Menu</span>
						<DrawerClose asChild>
							<button className="flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-[var(--paper-2)]" style={{ background: 'transparent', border: 0, cursor: 'pointer' }}>
								<Icon name="cross-1" className="h-5 w-5" />
							</button>
						</DrawerClose>
					</div>

					{/* Nav links — large display font with numbers */}
					<div className="flex flex-1 flex-col gap-1">
						{links.map((l, i) => (
							<DrawerClose asChild key={l.to}>
								<Link
									to={l.to}
									className="font-heading flex items-baseline justify-between py-2 text-[clamp(32px,6vw,44px)] font-medium tracking-tight transition-[padding,color] duration-[180ms] hover:pl-3"
									style={{
										color: 'var(--ink)',
										textDecoration: 'none',
										letterSpacing: '-0.02em',
										borderBottom: '1px solid var(--line)',
										transitionTimingFunction: 'var(--ease)',
									}}
									onClick={() => setOpen(false)}
								>
									{l.label}
									<span className="text-[11px] tracking-[0.12em]" style={{ color: 'var(--muted-gallery)', fontFamily: 'var(--font-sans)' }}>
										0{i + 1}
									</span>
								</Link>
							</DrawerClose>
						))}
					</div>

					{/* Footer */}
					<div className="mt-8 flex flex-col gap-4 text-[13px]" style={{ color: 'var(--muted-gallery)' }}>
						{user ? (
							<>
								{isAdmin && (
									<DrawerClose asChild>
										<Link to="/admin/inquiries" onClick={() => setOpen(false)} style={{ color: 'var(--ink)', textDecoration: 'none', cursor: 'pointer', fontWeight: 500 }}>
											Beheer
										</Link>
									</DrawerClose>
								)}
								<DrawerClose asChild>
									<Link to={`/users/${user.username}`} onClick={() => setOpen(false)} style={{ color: 'var(--ink)', textDecoration: 'none', cursor: 'pointer' }}>
										{user.name || user.username}
									</Link>
								</DrawerClose>
								<DrawerClose asChild>
									<form action="/logout" method="POST">
										<button type="submit" style={{ background: 'none', border: 'none', color: 'var(--ink)', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 13 }}>
											{t('logout')}
										</button>
									</form>
								</DrawerClose>
							</>
						) : (
							<DrawerClose asChild>
								<Link to="/login" onClick={() => setOpen(false)} style={{ color: 'var(--ink)', textDecoration: 'none', cursor: 'pointer' }}>
									{t('login')}
								</Link>
							</DrawerClose>
						)}
						<div className="flex gap-6 pt-2">
							<DrawerClose asChild>
								<Link to="/privacy" onClick={() => setOpen(false)} style={{ color: 'var(--ink)', textDecoration: 'none', cursor: 'pointer' }}>{t('privacy')}</Link>
							</DrawerClose>
							<DrawerClose asChild>
								<Link to="/tos" onClick={() => setOpen(false)} style={{ color: 'var(--ink)', textDecoration: 'none', cursor: 'pointer' }}>{t('terms')}</Link>
							</DrawerClose>
						</div>
						<div style={{ fontSize: 11, letterSpacing: '0.1em' }}>Atelier · Utrecht · NL</div>
					</div>
				</nav>
			</DrawerContent>
		</Drawer>
	)
}
